import {
  ExercisePhase,
  ExerciseResult,
} from '@pelckmans/business-components/components/exercise-manager/exercise-types';
import { ComponentProps, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ValuesOfObject } from '../../../@types/utils';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../constants/analyticsEvents';
import GoogleAnalyticsContext from '../../../contexts/GoogleAnalyticsContext';
import IdentityContext from '../../../contexts/IdentityContext';
import ModalContext from '../../../contexts/ModalContext';
import useAnalytics from '../../../hooks/useAnalytics';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { getNodeDescription } from '../../../utils/getNodeDescription';
import { checkIsExercise, getLinkFromMedialink } from '../../../utils/medialink';
import {
  isExternalMedia,
  isFile,
  isMedialinkStudentAssignment,
  isMiniDialog,
  isMiniSite,
  isScorableMedialink,
  isWordlist,
  Medialink,
  MedialinkReviewState,
  updateMedialinkReviewState,
} from '../_api/MedialinksApi';
import { Module } from '../_api/ModuleApi';
import { Node, NodeWithoutDescription } from '../_api/TableOfContentsApi';
import Score from '../_components/common/Score';
import MedialinksContext from '../_contexts/MedialinksContext';
import ExerciseModal from '../_modals/ExerciseModal';
import MaterialAssignmentModal from '../_modals/MaterialAssignmentModal';
import MiniDialogModal from '../_modals/MiniDialogModal';
import NotAuthenticatedModal from '../_modals/NotAuthenticatedModal';
import NotLicensedModuleModal from '../_modals/NotLicensedModuleModal';
import ReviewModal from '../_modals/ReviewModal';
import WordlistModal from '../_wordlist/WordlistModal';
import useExternalMediaMedialink from './medialink/useExternalMediaMedialink';
import useFileMedialink from './medialink/useFileMedialink';
import useMiniSiteMedialink from './medialink/useMiniSiteMedialink';

export default function useMedialinks() {
  const { REACT_APP_P_STUDIO_GROUP } = process.env;

  const { identity } = useContext(IdentityContext);
  const { medialinks, setMedialinks } = useContext(MedialinksContext);
  const { showModal } = useContext(ModalContext);
  const ga = useContext(GoogleAnalyticsContext);

  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { openFileMedialink } = useFileMedialink();
  const { openMiniSiteMedialink } = useMiniSiteMedialink();
  const { openExternalMediaMedialink } = useExternalMediaMedialink();
  const handleError = useThrowAsyncError();

  const getTargetAudience = (medialink: Medialink) => {
    if (identity.isTeacherAlike && medialink.group) {
      return medialink.group === 'teacher' ? 'ROLES.TEACHER' : 'ROLES.STUDENT';
    }
  };

  const getReviewState = (medialink: Medialink) => {
    const translation = t('MODULES.MATERIAL.COMPONENTS.MEDIALINKS.REVIEW.CONTROL');

    switch (medialink.reviewState) {
      case MedialinkReviewState.NOT_IN_REVIEW:
        return null;
      case MedialinkReviewState.IN_REVIEW:
        return (
          <span className="pill pill--warning" role="status">
            {translation}
          </span>
        );
      case MedialinkReviewState.ACCEPTED:
        return (
          <span className="pill pill--success" role="status">
            {translation}
          </span>
        );
      case MedialinkReviewState.DECLINED:
        return (
          <span className="pill pill--danger" role="status">
            {translation}
          </span>
        );
    }
  };

  const showScore = (medialink: Medialink) => {
    return (
      identity.isAuthenticated &&
      checkIsExercise(medialink.kind) &&
      ['SO', 'ES', 'VO'].includes(REACT_APP_P_STUDIO_GROUP)
    );
  };

  const getScore = (medialink: Medialink) => {
    if (!showScore(medialink)) return null;

    return <Score score={medialink.score} />;
  };

  const getDisabled = (medialink: Medialink) => {
    return (
      !medialink.file &&
      !medialink.externalMedia &&
      !medialink.edumaticExercise &&
      !medialink.eduhintExercise &&
      !medialink.assessmentQExercise &&
      !medialink.miniDialog &&
      !medialink.wordList &&
      !medialink.miniSite
    );
  };

  const updateResult = useCallback(
    (medialinkId: string, result: ExerciseResult) => {
      setMedialinks(medialinks =>
        medialinks.map(medialink => {
          if (medialink.id === medialinkId && isScorableMedialink(medialink)) {
            return {
              ...medialink,
              score: {
                amountOfItems: medialink.amountOfItems,
                ...medialink.score,
                ...result.score,
              },
            };
          }
          return medialink;
        }),
      );
    },
    [setMedialinks],
  );

  const handleSessionPaused = useCallback(
    (medialinkId: string, result: ExerciseResult) => {
      updateResult(medialinkId, result);
    },
    [updateResult],
  );

  const handleSessionFinished = useCallback(
    (medialinkId: string, result: ExerciseResult) => {
      updateResult(medialinkId, result);

      // this should later update the count of assignments indicator
    },
    [updateResult],
  );

  const openMaterial = useCallback(
    async (
      medialink: Medialink,
      nodeWithoutDescription: NodeWithoutDescription,
      module: Module,
      subLocationId?: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>,
    ) => {
      const node: Node = {
        ...nodeWithoutDescription,
        description: getNodeDescription(nodeWithoutDescription, t),
      };

      if (ga) {
        ga.capture({
          category: 'Material',
          action: 'MedialinkClick',
          label: medialink.name,
          moduleName: module.name,
          medialinkType: medialink.kind,
        });
      }

      if (subLocationId) {
        await analytics.capture({
          objectId: medialink.id,
          eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
          subLocationId,
        });
      }

      if (isWordlist(medialink)) {
        return (
          <WordlistModal
            id="wordlist-modal"
            node={node}
            wordlistId={medialink.wordList.id}
            medialink={medialink}
            name={medialink.name}
            module={module}
          />
        );
      }

      if (isMiniSite(medialink)) {
        return openMiniSiteMedialink(medialink, node, module.id);
      }

      if (isMiniDialog(medialink)) {
        // this id comes from the business components
        return <MiniDialogModal id="mini-dialog-modal" medialink={medialink} module={module} />;
      }

      if (isExternalMedia(medialink)) {
        return openExternalMediaMedialink(medialink, node);
      }

      if (isFile(medialink)) {
        return openFileMedialink(medialink, node);
      }
    },
    [analytics, openExternalMediaMedialink, openFileMedialink, openMiniSiteMedialink, t, ga],
  );

  const openFreeExerciseOrAssignment = useCallback(
    async (
      medialink: Medialink,
      nodeWithoutDescription: NodeWithoutDescription,
      module: Module,
      onClose?: () => void,
      exerciseInitialPhase?: ExercisePhase,
      subLocationId?: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>,
    ) => {
      if (!identity.isAuthenticated) {
        return showModal(<NotAuthenticatedModal id="not-authenticated-modal" />);
      }

      if (getDisabled(medialink)) {
        return (
          <NotLicensedModuleModal
            id="not-licensed-module-modal"
            action="view"
            license={identity.isTeacherAlike && medialink.group === 'teacher' ? 'teacher' : undefined}
          />
        );
      }

      if (isScorableMedialink(medialink)) {
        if (ga) {
          ga.capture({
            category: 'Material',
            action: 'MedialinkClick',
            label: medialink.name,
            moduleName: module.name,
            medialinkType: medialink.kind,
          });
        }

        if (subLocationId) {
          await analytics.capture({
            objectId: medialink.id,
            eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
            subLocationId,
          });
        }

        return (
          <ExerciseModal
            id="exercise-modal"
            medialink={medialink}
            module={module}
            onSessionPaused={result => handleSessionPaused(medialink.id, result)}
            onSessionFinished={result => handleSessionFinished(medialink.id, result)}
            onClose={onClose}
            initialPhase={exerciseInitialPhase}
          />
        );
      }

      if (isMedialinkStudentAssignment(medialink)) {
        const currentAssignment = medialink.assignments.find(x => x.current);

        if (!currentAssignment?.id) return null;

        const materialAssignment: ComponentProps<typeof MaterialAssignmentModal>['material'] = {
          ...medialink,
          type: 'medialink',
          owner: currentAssignment.user,
          assignment: currentAssignment,
          href: getLinkFromMedialink(medialink),
        };

        const handleOpenMaterial = async () => {
          const modal = await openMaterial(medialink, nodeWithoutDescription, module, subLocationId);

          if (modal) {
            return showModal(modal);
          }
        };

        return (
          <MaterialAssignmentModal
            id="material-assignment-modal"
            material={materialAssignment}
            onOpenMaterial={handleOpenMaterial}
            onModalClosed={() => {
              if (onClose) onClose();
            }}
          />
        );
      }
      return openMaterial(medialink, nodeWithoutDescription, module, subLocationId);
    },
    [
      analytics,
      handleSessionFinished,
      handleSessionPaused,
      identity.isTeacherAlike,
      openMaterial,
      showModal,
      identity.isAuthenticated,
      ga,
    ],
  );

  const openReview = async (
    medialink: Medialink,
    sessionId: string,
    module: Module,
    nodeWithoutDescription: NodeWithoutDescription,
    subLocationId?: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>,
  ) => {
    if (isScorableMedialink(medialink)) {
      if (subLocationId) {
        await analytics.capture({
          objectId: medialink.id,
          eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
          subLocationId,
        });
      }

      if (ga) {
        ga.capture({
          category: 'Material',
          action: 'MedialinkClick',
          label: medialink.name,
          moduleName: module.name,
          medialinkType: medialink.kind,
        });
      }

      return <ReviewModal id="review-modal" medialink={medialink} sessionId={sessionId} module={module} />;
    }

    return openFreeExerciseOrAssignment(medialink, nodeWithoutDescription, module, undefined, undefined, subLocationId);
  };

  const updateReviewState = async (medialinkId: string, reviewState: MedialinkReviewState, moduleId: string) => {
    try {
      await updateMedialinkReviewState(medialinkId, reviewState, moduleId);

      setMedialinks(medialinks =>
        medialinks.map(current => {
          if (current.id !== medialinkId) return current;
          return { ...current, reviewState };
        }),
      );
    } catch (err) {
      handleError(err);
    }
  };

  const updateShareInfo = async (
    medialinkId: string,
    shares: Medialink['shares'],
    assignments: Medialink['assignments'],
  ) => {
    setMedialinks(medialinks =>
      medialinks.map(current => {
        if (current.id !== medialinkId) return current;
        return { ...current, shares, assignments };
      }),
    );
  };

  return {
    medialinks,
    getTargetAudience,
    getReviewState,
    getScore,
    getDisabled,
    openMedialink: openFreeExerciseOrAssignment,
    openReview,
    updateReviewState,
    updateShareInfo,
  };
}
